import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import type { Effect } from 'redux-saga/effects';
import Api from 'src/helpers/api';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import notifications from 'src/redux/notifications/actions';
import actions from './actions';
import type {
  FetchOptionsRequestAction,
  FetchOverviewRequestAction,
  FetchPairRequestAction,
  FetchPositionRequestAction,
} from './actions';
import actionTypes from './actionTypes';
import { toOptions, toOverview, toPairDetail, toPositionDetail } from './adapter';
import {
  LIST_PAGE_SIZE,
  OPTION_LIST_PATHS,
  OPTION_LISTS,
  OVERVIEW_PATH,
  PAIRS_PATH,
  POSITIONS_PATH,
} from './constants';
import { toOptionsRequestKey } from './helpers';
import type { IQuery, RawOptionPage, RawOverview, RawPairDetail, RawPositionDetail } from './types';

type GetResponse<T> = { status?: number; data?: { success?: { data?: T | null } | null } | null } | undefined;

type RequestSaga<T> = Generator<Effect, void, GetResponse<T>>;

const listApis = {
  [OPTION_LISTS.UNIT]: new Api(OPTION_LIST_PATHS[OPTION_LISTS.UNIT]),
};
const overviewApi = new Api(OVERVIEW_PATH);
const pairsApi = new Api(PAIRS_PATH);

const readData = <T>(result: GetResponse<T>): T | null =>
  typeof result?.status === 'number' && result.status >= 200 && result.status < 300
    ? result.data?.success?.data ?? null
    : null;

const queryParams = ({ companyId, date, compareDate }: IQuery) => ({
  company_id: companyId,
  date,
  ...(compareDate ? { compare_date: compareDate } : {}),
});

export function* fetchOptions({ payload }: FetchOptionsRequestAction): RequestSaga<RawOptionPage> {
  const key = toOptionsRequestKey(payload);
  const { list, search, page } = key;

  try {
    const params = { ...(search ? { search } : {}), page, page_size: LIST_PAGE_SIZE };
    const result = yield call(sendRequest, listApis[list].load, { search: params }, notifications.REQUEST_ERROR);
    const data = readData(result);

    if (data && Array.isArray(data.records)) {
      yield put(
        actions.fetchOptionsSuccess({
          ...key,
          options: toOptions(data.records),
          nextPage: data.pagination?.next_page ?? null,
          append: page > 1,
        }),
      );

      return;
    }

    yield put(actions.fetchOptionsError(key));
  } catch (error) {
    logger.error(error);
    yield put(actions.fetchOptionsError(key));
  }
}

export function* fetchOverview({ payload }: FetchOverviewRequestAction): RequestSaga<RawOverview> {
  try {
    const result = yield call(
      sendRequest,
      overviewApi.load,
      { search: queryParams(payload) },
      notifications.REQUEST_ERROR,
    );
    const data = readData(result);

    if (data) {
      yield put(actions.fetchOverviewSuccess(toOverview(data)));

      return;
    }

    yield put(actions.fetchOverviewError());
  } catch (error) {
    logger.error(error);
    yield put(actions.fetchOverviewError());
  }
}

export function* fetchPosition({ payload }: FetchPositionRequestAction): RequestSaga<RawPositionDetail> {
  try {
    const positionApi = new Api(`${POSITIONS_PATH}/${payload.positionId}`);
    const result = yield call(
      sendRequest,
      positionApi.load,
      { search: queryParams(payload) },
      notifications.REQUEST_ERROR,
    );
    const data = readData(result);

    if (data) {
      yield put(actions.fetchPositionSuccess(toPositionDetail(data)));

      return;
    }

    yield put(actions.fetchPositionError());
  } catch (error) {
    logger.error(error);
    yield put(actions.fetchPositionError());
  }
}

export function* fetchPair({ payload }: FetchPairRequestAction): RequestSaga<RawPairDetail> {
  try {
    const result = yield call(
      sendRequest,
      pairsApi.load,
      {
        search: {
          ...queryParams(payload),
          position_id: payload.positionId,
          agency_contract_id: payload.agencyContractId,
        },
      },
      notifications.REQUEST_ERROR,
    );
    const data = readData(result);

    if (data) {
      yield put(actions.fetchPairSuccess(toPairDetail(data)));

      return;
    }

    yield put(actions.fetchPairError());
  } catch (error) {
    logger.error(error);
    yield put(actions.fetchPairError());
  }
}

export default function* rootSaga(): Generator<Effect, void, unknown> {
  yield all([
    takeEvery(actionTypes.FETCH_OPTIONS_REQUEST, fetchOptions),
    takeLatest(actionTypes.FETCH_OVERVIEW_REQUEST, fetchOverview),
    takeLatest(actionTypes.FETCH_POSITION_REQUEST, fetchPosition),
    takeLatest(actionTypes.FETCH_PAIR_REQUEST, fetchPair),
  ]);
}
